// File Metadata:
// Filename: CrossdockList.js
// Version: 1.00
// Created: 2025-03-22 00:06:12
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import React, { useState } from 'react'
import { useGetCrossdocksQuery, useUpdateCrossdockMutation, useDeleteCrossdockMutation } from '../api/crossdockApiSlice'
import { Box, Card, CardContent, Typography, Button, Container, Stack, ImageList, ImageListItem, Modal, CardActions, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemText, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Grid, CircularProgress, Checkbox, FormControlLabel, Paper, Collapse, IconButton } from '@mui/material'
import { useNavigate, Link } from 'react-router-dom'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CallMadeIcon from '@mui/icons-material/CallMade'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import useAuth from '../../hooks/useAuth'

const CrossdockList = () => {
    const { isManager, isAdmin, isCoordinator } = useAuth()
    const [deleteCrossdock] = useDeleteCrossdockMutation()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [selectedId, setSelectedId] = useState(null)
    const navigate = useNavigate()
    const [selectedImage, setSelectedImage] = useState(null)
    const [filterStatus, setFilterStatus] = useState('active') // Changed default to 'active'
    const [sortBy, setSortBy] = useState('date') // 'date', 'tour'
    const [searchQuery, setSearchQuery] = useState('')
    const [sortDirection, setSortDirection] = useState('desc')
    const [quickAssignOpen, setQuickAssignOpen] = useState(false)
    const [selectedCrossdock, setSelectedCrossdock] = useState(null)
    const [departureTour, setDepartureTour] = useState('')
    const [selectedItems, setSelectedItems] = useState([])
    const [batchAssignOpen, setBatchAssignOpen] = useState(false)
    const [batchDepartureTour, setBatchDepartureTour] = useState('')
    const [updateCrossdock] = useUpdateCrossdockMutation()
    const [filtersExpanded, setFiltersExpanded] = useState(false)
    const {
        data: crossdocks,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetCrossdocksQuery()

    const onNewArrivalClicked = () => navigate('/dash/crossdock/arrival/new')
    const onNewDepartureClicked = () => navigate('/dash/crossdock/departure/new')

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl)
    }

    const handleCloseModal = () => {
        setSelectedImage(null)
    }

    const canDelete = isManager || isAdmin || isCoordinator
    const canAssignTour = isManager || isAdmin || isCoordinator

    const onDeleteClicked = async (id) => {
        try {
            await deleteCrossdock({ id }).unwrap()
        } catch (err) {
            console.error('Failed to delete crossdock:', err)
        }
    }

    const handleDeleteClick = (id) => {
        setSelectedId(id)
        setDeleteDialogOpen(true)
    }

    const handleDeleteConfirm = () => {
        if (selectedId) {
            onDeleteClicked(selectedId)
            setDeleteDialogOpen(false)
            setSelectedId(null)
        }
    }

    const handleQuickAssign = (crossdock) => {
        setSelectedCrossdock(crossdock)
        setDepartureTour(crossdock.departureTour || '')
        setQuickAssignOpen(true)
    }

    const handleAssignSubmit = async () => {
        if (selectedCrossdock && departureTour) {
            try {
                await updateCrossdock({
                    id: selectedCrossdock.id,
                    departureTour
                }).unwrap()
                setQuickAssignOpen(false)
                setSelectedCrossdock(null)
                setDepartureTour('')
            } catch (err) {
                console.error('Failed to update departure tour:', err)
            }
        }
    }

    const handleBatchSelect = (crossdockId) => {
        setSelectedItems(prev => 
            prev.includes(crossdockId)
                ? prev.filter(id => id !== crossdockId)
                : [...prev, crossdockId]
        )
    }

    const handleBatchAssign = async () => {
        if (batchDepartureTour && selectedItems.length > 0) {
            try {
                await Promise.all(
                    selectedItems.map(id => 
                        updateCrossdock({
                            id,
                            departureTour: batchDepartureTour
                        }).unwrap()
                    )
                )
                setBatchAssignOpen(false)
                setBatchDepartureTour('')
                setSelectedItems([])
            } catch (err) {
                console.error('Failed to update departure tours:', err)
            }
        }
    }

    const filterAndSortCrossdocks = (crossdocks) => {
        if (!crossdocks?.ids?.length) return []
        
        let filteredIds = [...crossdocks.ids]
        
        // Apply status filter
        if (filterStatus !== 'all') {
            filteredIds = filteredIds.filter(id => 
                filterStatus === 'departed' 
                    ? crossdocks.entities[id].departed 
                    : !crossdocks.entities[id].departed
            )
        }

        // Apply search filter
        if (searchQuery) {
            filteredIds = filteredIds.filter(id => {
                const record = crossdocks.entities[id]
                return record.handlingUnits.some(hu => 
                    hu.sscc.toLowerCase().includes(searchQuery.toLowerCase())
                ) ||
                record.arrivalTour.toLowerCase().includes(searchQuery.toLowerCase())
            })
        }

        // Apply sorting
        filteredIds.sort((a, b) => {
            const recordA = crossdocks.entities[a]
            const recordB = crossdocks.entities[b]
            
            if (sortBy === 'date') {
                return sortDirection === 'desc'
                    ? new Date(recordB.createdAt) - new Date(recordA.createdAt)
                    : new Date(recordA.createdAt) - new Date(recordB.createdAt)
            }
            
            if (sortBy === 'tour') {
                return sortDirection === 'desc'
                    ? recordB.arrivalTour.localeCompare(recordA.arrivalTour)
                    : recordA.arrivalTour.localeCompare(recordB.arrivalTour)
            }
            
            return 0
        })

        return filteredIds
    }

    const desktopFilters = (
        <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 2,
            alignItems: 'center',
            mb: 2
        }}>
            <FormControl sx={{ width: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                >
                    <MenuItem value="all">All Records</MenuItem>
                    <MenuItem value="active">Active Only</MenuItem>
                    <MenuItem value="departed">Departed Only</MenuItem>
                </Select>
            </FormControl>

            <FormControl sx={{ width: 200 }}>
                <InputLabel>Sort By Date</InputLabel>
                <Select
                    value={sortDirection}
                    label="Sort By Date"
                    onChange={(e) => setSortDirection(e.target.value)}
                >
                    <MenuItem value="desc">Newest First</MenuItem>
                    <MenuItem value="asc">Oldest First</MenuItem>
                </Select>
            </FormControl>

            <TextField
                placeholder="Search Tours/Docks"
                variant="outlined"
                size="medium"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 200 }}
                InputProps={{
                    startAdornment: <SearchIcon />,
                    sx: { height: '56px' }  // Match height with other components
                }}
            />

            <Box sx={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={onNewArrivalClicked}
                    sx={{ height: '56px' }}
                >
                    Register Arrival
                </Button>
                <Button
                    variant="contained"
                    startIcon={<CallMadeIcon />}
                    onClick={onNewDepartureClicked}
                    sx={{ height: '56px' }}
                >
                    Register Departure
                </Button>
            </Box>
        </Box>
    )

    const mobileFilters = (
        <Box sx={{ 
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            gap: 2,
            mb: 2
        }}>
            <Paper sx={{ width: '100%' }}>
                <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                    sx={{ cursor: 'pointer', p: 2 }}
                >
                    <Typography>Filters</Typography>
                    <IconButton>
                        {filtersExpanded ? <KeyboardArrowUpIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Stack>

                <Collapse in={filtersExpanded}>
                    <Box sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Status"
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <MenuItem value="all">All Records</MenuItem>
                                    <MenuItem value="active">Active Only</MenuItem>
                                    <MenuItem value="departed">Departed Only</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Sort By Date</InputLabel>
                                <Select
                                    value={sortDirection}
                                    label="Sort By Date"
                                    onChange={(e) => setSortDirection(e.target.value)}
                                >
                                    <MenuItem value="desc">Newest First</MenuItem>
                                    <MenuItem value="asc">Oldest First</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                placeholder="Search Tours/Docks"
                                variant="outlined"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon />
                                }}
                            />
                        </Stack>
                    </Box>
                </Collapse>
            </Paper>

            {/* Action Buttons for Mobile */}
            <Stack direction="column" spacing={2}>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={onNewArrivalClicked}
                    sx={{ height: '56px' }}
                >
                    Register Arrival
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CallMadeIcon />}
                    onClick={onNewDepartureClicked}
                    sx={{ height: '56px' }}
                >
                    Register Departure
                </Button>
            </Stack>
        </Box>
    )

    let listContent

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        )
    }

    if (isError) {
        listContent = <Card><CardContent><Typography color="error">{error?.data?.message || 'Error loading data'}</Typography></CardContent></Card>
    }

    if (isSuccess) {
        const filteredIds = filterAndSortCrossdocks(crossdocks)
        
        listContent = filteredIds.length 
            ? filteredIds.map(crossdockId => {
                const crossdock = crossdocks.entities[crossdockId]
                return (
                    <Card key={crossdockId}>
                        <CardContent>
                            {!crossdock.departed && canAssignTour && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedItems.includes(crossdockId)}
                                            onChange={() => handleBatchSelect(crossdockId)}
                                        />
                                    }
                                    label="Select for batch assign"
                                />
                            )}
                            <Typography variant="h6">Tour: {crossdock.arrivalTour}</Typography>
                            <Box sx={{ ml: 2, mb: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>Handling Units:</Typography>
                                <List dense>
                                    {crossdock.handlingUnits.map((hu, index) => (
                                        <ListItem key={index}>
                                            <ListItemText
                                                primary={`HU: ${hu.sscc}`}
                                                secondary={`Type: ${hu.packageType}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                            <Typography>Location: {crossdock.location}</Typography>
                            <Typography>Status: {crossdock.departed ? 'Departed' : 'In Storage'}</Typography>
                            {crossdock.departureTour && (
                                <Typography>Departure Tour: {crossdock.departureTour}</Typography>
                            )}
                            {crossdock.destination && (
                                <Typography>Destination: {crossdock.destination}</Typography>
                            )}
                            {(crossdock.images?.length > 0 || crossdock.departureImages?.length > 0) && (
                                <ImageList sx={{ width: '100%', height: 100 }} cols={4} rowHeight={100}>
                                    {[...(crossdock.images || []), ...(crossdock.departureImages || [])].map((img, index) => (
                                        <ImageListItem 
                                            key={index} 
                                            onClick={() => handleImageClick(img.url)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <img
                                                src={`${img.url}?tr=w-100,h-100`}
                                                alt={`Crossdock ${index + 1}`}
                                                loading="lazy"
                                            />
                                        </ImageListItem>
                                    ))}
                                </ImageList>
                            )}
                        </CardContent>
                        <CardActions>
                            {!crossdock.departed && canAssignTour && (
                                <Button
                                    size="small"
                                    onClick={() => handleQuickAssign(crossdock)}
                                >
                                    Set Tour
                                </Button>
                            )}
                            <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/dash/crossdock/${crossdockId}`)}
                            >
                                Edit
                            </Button>
                            {canDelete && (
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteClick(crossdockId)}
                                >
                                    Delete
                                </Button>
                            )}
                        </CardActions>
                    </Card>
                )
            })
            : <Card><CardContent><Typography>No matching records found</Typography></CardContent></Card>
    }

    const content = (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ color: 'gray', mb: 2 }}>
                Crossdock Operations
            </Typography>

            {desktopFilters}
            {mobileFilters}

            {listContent}
        </Box>
    )

    return (
        <Container maxWidth="md">
            <Stack spacing={3}>
                {content}
            </Stack>
            <Modal
                open={!!selectedImage}
                onClose={handleCloseModal}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <img
                    src={selectedImage}
                    alt="Full size"
                    style={{ maxWidth: '90%', maxHeight: '90vh' }}
                />
            </Modal>
            <Dialog
                open={quickAssignOpen}
                onClose={() => setQuickAssignOpen(false)}
            >
                <DialogTitle>Set Departure Tour</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Departure Tour"
                            value={departureTour}
                            onChange={(e) => setDepartureTour(e.target.value)}
                            placeholder="Enter tour number"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQuickAssignOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleAssignSubmit} 
                        variant="contained" 
                        disabled={!departureTour}
                    >
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this crossdock record? This will also delete all associated images.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={batchAssignOpen}
                onClose={() => setBatchAssignOpen(false)}
            >
                <DialogTitle>Assign Departure Tour to {selectedItems.length} Items</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Departure Tour"
                            value={batchDepartureTour}
                            onChange={(e) => setBatchDepartureTour(e.target.value)}
                            placeholder="Enter tour number"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBatchAssignOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleBatchAssign} 
                        variant="contained" 
                        disabled={!batchDepartureTour}
                    >
                        Assign to All
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default CrossdockList
